import React from 'react';
import type { TagTone } from '../components/ui/Tag';
import Skeleton from '../components/ui/Skeleton';
import type { Severity } from './types';

/* The Analyst page's vocabulary, defined once. The page, the chat, the workspace and the
   memories modal all import from here, so no file imports the page. */

export const MONO = '[font-family:RobotoMono,ui-monospace,monospace]';
export const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
export const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';
export const PANEL = 'flex min-w-0 flex-col overflow-hidden rounded-lg bg-surface-container-low';
export const PANEL_HEADER = 'flex min-w-0 flex-shrink-0 items-center justify-between gap-2 bg-surface-container px-3 py-2';
export const PANEL_TITLE = 'min-w-0 truncate text-sm font-semibold text-on-surface';
export const QUIET_ACTION = `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface disabled:opacity-40 ${FOCUS} ${MOTION}`;
export const FILLED_PILL = `inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-on-primary hover:bg-primary/90 disabled:opacity-40 ${FOCUS} ${MOTION}`;
export const TONAL_DANGER = `inline-flex items-center whitespace-nowrap rounded-full bg-red-500/15 px-3 py-1 text-xs font-semibold text-red-300 hover:bg-red-500/25 ${FOCUS} ${MOTION}`;
export const ICON_BUTTON = `inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface ${FOCUS} ${MOTION}`;
export const CHIP = 'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs';

/** A severity's tag tone: the word names it, the tone only reinforces it. */
export const SEVERITY_TONE: Record<Severity, TagTone> = { info: 'neutral', advisory: 'info', warning: 'warn', critical: 'bad' };

/** Placeholder bars: the shape of what is loading. */
export const SkeletonRows: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="flex flex-col gap-2 p-3" aria-hidden="true">
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Skeleton className="h-3 w-20 rounded" />
        <Skeleton className="h-3 flex-1 rounded" />
      </div>
    ))}
  </div>
);

export const CloseIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path fill="currentColor" d="M3.7 2.3 8 6.6l4.3-4.3 1.4 1.4L9.4 8l4.3 4.3-1.4 1.4L8 9.4l-4.3 4.3-1.4-1.4L6.6 8 2.3 3.7z" />
  </svg>
);

